import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Ownership() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [reassignFor, setReassignFor] = useState(null);
  const [newOwner, setNewOwner] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = async () => {
    const [o, u] = await Promise.all([api.get("/dashboard/ownership"), api.get("/users")]);
    setRows(o.data.rows);
    setUsers(u.data);
  };

  useEffect(() => {
    (async () => {
      try {
        await load();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const doReassign = async () => {
    if (!newOwner || !reassignFor) return;
    await api.patch(`/sops/${reassignFor.sop_id}`, { owner_id: newOwner });
    setReassignFor(null);
    setNewOwner("");
    load();
  };

  const canReassign = user?.role === "admin" || user?.role === "owner";

  return (
    <div className="space-y-6" data-testid="ownership-page">
      <header>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-white">Ownership</h1>
        <p className="text-[#9AA4B2] text-sm mt-1">Every SOP has a named owner and next-review date. Reassign in one click.</p>
      </header>

      <GlassCard className="p-0 overflow-hidden">
        <div className="overflow-x-auto w-full">
          <table className="w-full text-sm min-w-[700px]" data-testid="ownership-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#5B6472] border-b border-white/10">
              <th className="py-3 px-5">SOP</th>
              <th className="py-3 px-5">Status</th>
              <th className="py-3 px-5">Owner</th>
              <th className="py-3 px-5">Expires</th>
              <th className="py-3 px-5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-10 text-center text-[#5B6472]">Loading…</td></tr>
            ) : rows.map((r) => (
              <tr key={r.sop_id} className={`border-b border-white/5 hover:bg-white/[0.03] transition ${r.overdue ? "bg-rose-500/[0.05]" : ""}`}>
                <td className="py-3 px-5">
                  <button className="text-white hover:underline text-left" onClick={() => navigate(`/sops/${r.sop_id}`)}>
                    {r.title}
                  </button>
                </td>
                <td className="py-3 px-5"><StatusBadge status={r.status} /></td>
                <td className="py-3 px-5 text-[#9AA4B2]">{r.owner?.name || "—"}</td>
                <td className={`py-3 px-5 ${r.overdue ? "text-rose-300" : "text-[#9AA4B2]"}`}>
                  {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"} {r.overdue && "· overdue"}
                </td>
                <td className="py-3 px-5 text-right">
                  {canReassign && (
                    <Button
                      variant="ghost"
                      onClick={() => setReassignFor(r)}
                      className="btn-ghost-glass rounded-full px-4 text-xs"
                      data-testid={`reassign-btn-${r.sop_id}`}
                    >
                      <UserRound size={12} className="mr-1" /> Reassign
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </GlassCard>

      <Dialog open={!!reassignFor} onOpenChange={(o) => !o && setReassignFor(null)}>
        <DialogContent className="glass-strong bg-[#0A0E1A]/90 text-white border-white/10 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Reassign owner</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#9AA4B2]">Pick a new owner for <span className="text-white">{reassignFor?.title}</span>.</div>
          <select
            value={newOwner}
            onChange={(e) => setNewOwner(e.target.value)}
            className="mt-2 w-full bg-white/[0.04] border border-white/10 rounded-2xl px-3 py-2.5 text-white focus:outline-none focus:border-cyan-400"
            data-testid="reassign-select"
          >
            <option value="">Select…</option>
            {users.filter((u) => u.role !== "user").map((u) => (
              <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
            ))}
          </select>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" className="btn-ghost-glass rounded-full" onClick={() => setReassignFor(null)}>Cancel</Button>
            <Button className="btn-primary-glow rounded-full" onClick={doReassign} data-testid="reassign-confirm">
              Reassign
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
